import { cn } from "@/lib/utils";
import { CSSProperties, memo, useEffect, useState } from "react";

type LetterStatus = "correct" | "wrong-position" | "incorrect" | "empty" | "completed" | "invalid";

interface LetterBoxProps {
  letter?: string;
  status?: LetterStatus;
  index?: number;
  isShaking?: boolean;
  isAnswer?: boolean;
  isCompact?: boolean;
  isPlaceholder?: boolean;
  animationDelay?: string;
}

export const LetterBox = memo(({ 
  letter = "", 
  status = "empty",
  index = 0,
  isShaking = false,
  isAnswer = false,
  isCompact = false,
  isPlaceholder = false,
  animationDelay = "0s"
}: LetterBoxProps) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  
  // Check for small screen on mount and window resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 375);
    };
    
    // Initial check
    checkScreenSize();
    
    // Set up the resize listener
    window.addEventListener('resize', checkScreenSize);
    
    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Effect to handle animations - ensures component stays mounted
  useEffect(() => {
    // For animation coordination, we ensure components stay mounted
    // but might become invisible for smoother transitions
    setIsVisible(true);
  }, [letter, status]);

  // Fixed dimensions to prevent the "bounce" effect on small screens
  const smallScreenSize = "30px";
  const regularSize = isCompact ? "34px" : "40px";
  
  const baseStyles: CSSProperties = {
    display: isVisible ? "flex" : "none",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: isCompact 
      ? (isSmallScreen ? "0.75rem" : "0.875rem") 
      : (isSmallScreen ? "0.85rem" : "1rem"),
    borderStyle: "solid",
    borderWidth: "2px",
    boxSizing: "border-box",
    width: isSmallScreen ? smallScreenSize : regularSize,
    height: isSmallScreen ? smallScreenSize : regularSize,
    minWidth: isSmallScreen ? smallScreenSize : regularSize,
    minHeight: isSmallScreen ? smallScreenSize : regularSize,
    maxWidth: isSmallScreen ? smallScreenSize : regularSize,
    maxHeight: isSmallScreen ? smallScreenSize : regularSize,
    color: "white",
    borderRadius: "8px",
    transition: "all 0.15s ease-out", // Shorter transition time to reduce bouncing effect
    backfaceVisibility: "hidden",
  };

  // Generate proper class names
  const letterBoxClasses = cn(
    "letter-box",
    isCompact ? "compact-letter-box" : "",
    status === "correct" && !isAnswer && "bg-success border-success",
    status === "wrong-position" && "bg-warning border-warning",
    status === "incorrect" && "bg-destructive border-destructive",
    status === "empty" && (isPlaceholder ? "border-gray-600 bg-black" : "border-[#1f2937]/60 bg-black"),
    status === "completed" && !isAnswer && "bg-primary border-primary", 
    status === "invalid" && "border-[#ff4d4d]",
    isShaking ? "animate-shake border-primary" : "",
    isAnswer ? "answer-box bg-[#ea384c] border-[#ea384c]" : "",
    isSmallScreen ? "xs:text-xs" : "",
  );

  // Display the letter
  const displayLetter = letter ? letter.toUpperCase() : "";

  return (
    <div 
      className={letterBoxClasses} 
      style={baseStyles}
      data-status={status}
      data-answer={isAnswer ? "true" : undefined}
      data-compact={isCompact ? "true" : undefined}
      data-small-screen={isSmallScreen ? "true" : undefined}
      data-index={index}
    >
      {displayLetter}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.letter === nextProps.letter &&
    prevProps.status === nextProps.status &&
    prevProps.isShaking === nextProps.isShaking &&
    prevProps.isAnswer === nextProps.isAnswer &&
    prevProps.isCompact === nextProps.isCompact &&
    prevProps.isPlaceholder === nextProps.isPlaceholder &&
    prevProps.animationDelay === nextProps.animationDelay
  );
});

LetterBox.displayName = 'LetterBox';
